import type { MobxDocument } from '../firestore-mobx/document'
import {
  ObservableModelWithDecorators,
  emptyCollection,
  emptyModel,
} from '../firestore-mobx/model'
import { empty, type FirestoreSlide } from '../firestore/Slide/schema'
import { SlideType } from '../firestore/Slide/types'
import type { FirebaseRepository } from './FirebaseRepository'
import { SlideQuestionType } from './SlideQuestionType'

// This is called SlideModel until the Slide type is removed
export class SlideModel extends ObservableModelWithDecorators<FirestoreSlide> {
  static empty(repository: FirebaseRepository) {
    return emptyModel(repository, this, empty)
  }

  static emptyCollection(repository: FirebaseRepository) {
    return emptyCollection(repository, this, empty)
  }

  constructor(
    repository: FirebaseRepository,
    doc: MobxDocument<FirestoreSlide>
  ) {
    super(repository, doc)
  }

  get compatibleQuestionTypes() {
    switch (this.data.slideType) {
      case SlideType.interactivePoll:
        return [
          SlideQuestionType.poll,
          SlideQuestionType.customPoll,
          SlideQuestionType.title,
          SlideQuestionType.suggestedTimer,
          SlideQuestionType.talkTrack,
          SlideQuestionType.moderatorBot,
          SlideQuestionType.graderBot,
          SlideQuestionType.tutorBot,
          SlideQuestionType.rolePlayBot,
          SlideQuestionType.moderationTask,
          SlideQuestionType.displayModerationTask,
        ]
      // end of session is only eligible for talk track if there are questions attached
      case SlideType.endOfSession:
        return [
          SlideQuestionType.multipleChoice,
          SlideQuestionType.sorting,
          SlideQuestionType.title,
          SlideQuestionType.suggestedTimer,
          SlideQuestionType.talkTrack,
        ]
      // BL-4567: Solo Quiz (soloQuiz) and Group Quiz (interactiveQuiz) drop
      // suggestedTimer — they are completed individually, so a countdown adds no
      // value. Poll keeps its timer; groupQuiz below is the "Group Sorting
      // Activity", not a quiz, and also keeps its timer. Kept in sync with
      // slideTypeSupportsSuggestedTimer.
      case SlideType.interactiveQuiz:
      case SlideType.soloQuiz:
        return [
          SlideQuestionType.multipleChoice,
          SlideQuestionType.sorting,
          SlideQuestionType.title,
          SlideQuestionType.talkTrack,
        ]
      case SlideType.groupQuiz:
        return [
          SlideQuestionType.sorting,
          SlideQuestionType.title,
          SlideQuestionType.suggestedTimer,
          SlideQuestionType.talkTrack,
        ]
      // no talk track for slides that are eligible for video
      case SlideType.video:
      case SlideType.processingData:
      case SlideType.partialProcessingData:
      case SlideType.dynamicIntroVideo:
        return [SlideQuestionType.title, SlideQuestionType.suggestedTimer]
      case SlideType.sortedSoloInput:
      case SlideType.sortedGroupInput:
      case SlideType.sortedGroupInteractive:
        return [
          SlideQuestionType.title,
          SlideQuestionType.sortableStringList,
          SlideQuestionType.talkTrack,
          SlideQuestionType.moderatorBot,
          SlideQuestionType.graderBot,
          SlideQuestionType.tutorBot,
          SlideQuestionType.rolePlayBot,
          SlideQuestionType.moderationTask,
          SlideQuestionType.displayModerationTask,
          SlideQuestionType.popUpInstructions,
        ]
      case SlideType.iframe:
        return [
          SlideQuestionType.title,
          SlideQuestionType.talkTrack,
          SlideQuestionType.suggestedTimer,
        ]
      case SlideType.animatedCards:
        return [
          SlideQuestionType.title,
          SlideQuestionType.talkTrack,
          SlideQuestionType.moderatorBot,
          SlideQuestionType.graderBot,
          SlideQuestionType.tutorBot,
          SlideQuestionType.rolePlayBot,
          SlideQuestionType.displayModerationTask,
          SlideQuestionType.moderationTask,
          SlideQuestionType.suggestedTimer,
        ]
      case SlideType.interactiveForm:
        return [
          SlideQuestionType.title,
          SlideQuestionType.interactiveFormElement,
          SlideQuestionType.formCompletionBot,
          SlideQuestionType.aiAgent,
          SlideQuestionType.moderationTask,
          SlideQuestionType.displayModerationTask,
          SlideQuestionType.talkTrack,
          SlideQuestionType.suggestedTimer,
        ]
      default:
        return [
          SlideQuestionType.title,
          SlideQuestionType.talkTrack,
          SlideQuestionType.moderatorBot,
          SlideQuestionType.graderBot,
          SlideQuestionType.tutorBot,
          SlideQuestionType.rolePlayBot,
          SlideQuestionType.displayModerationTask,
          SlideQuestionType.moderationTask,
          SlideQuestionType.suggestedTimer,
        ]
    }
  }

  get canHaveImage() {
    return slideTypeCanHaveImage(this.data.slideType)
  }

  get canHaveVideo() {
    return this.data.slideType === SlideType.video
  }

  get hasImage() {
    return this.canHaveImage && this.data.slideImageURL
  }

  get hasVideo() {
    return (
      [
        SlideType.video,
        SlideType.processingData,
        SlideType.partialProcessingData,
      ].includes(this.data.slideType) && this.data.slideVideoURL
    )
  }

  get isYouTube() {
    return (
      this.data.slideType === SlideType.video &&
      !!this.data.slideVideoYouTubeURL
    )
  }

  get durationOrErrorStr() {
    let value = this.data.slideDuration.toString()
    if (this.hasVideo || this.isYouTube) {
      if (this.data.slideVideoError) {
        value = this.data.slideVideoError
      } else if (this.data.slideVideoDuration) {
        value = this.data.slideVideoDuration!.toString()
      } else {
        value = ''
      }
    }
    return value
  }

  get hasError() {
    return this.data.slideType === SlideType.video && this.data.slideVideoError
  }

  get slideMarkdown() {
    return this.data.slideMarkdown
  }
}

export const slideTypeCanHaveImage = (type: SlideType) => {
  const eligible = [
    SlideType.professorFeedback,
    SlideType.discussion,
    SlideType.image,
    SlideType.processingData,
    SlideType.partialProcessingData,
  ]
  return eligible.includes(type)
}
